/**
 * 贷款还款的**端侧纯判定**（`DR-049` · 批次 84）
 *
 * 依据：`Q域PRD` `AC-Q2.7` / `Q2.8` · `F域PRD` `AC-F1.19` / `F1.20` / `F1.21` / `F1.23` ·
 * `DR-045` D3（还款 = 人工三腿）· `DR-047` D5（贷款不走通用结算）· `AC-Q6.4`（端侧零自算）
 *
 * ## 🔴 本文件一句算术都没有
 *
 * | 事情 | 谁做 |
 * |---|---|
 * | 每期本金 / 利息拆分 | **服务端内核** `amortizeSplits`（端侧**绝不**调它） |
 * | 提前还款后的重排（缩期 / 降额） | **服务端内核** `rescheduleSplits` |
 * | 期次状态翻转 / 结算行 / 三条腿 | **服务端** `LoanScheduleService.pay()` / `prepay()` |
 * | 金额文本 → 整数分 | 共享内核 `yuanTextToCents()` |
 *
 * ⇒ 本文件只做四件事：**把表单打成请求** · **默认勾哪一期** · **判断哪些校验端侧能做** · **给提示文案**。
 *
 * ## 🔴 为什么"提前还款不给试算数字"（`DR-049` D8）
 *
 * 「缩期后每月还多少」要重跑一遍 `rescheduleSplits`。端侧自己算 = 把那个内核抄第二份
 * （`AC-Q6.4` 明确禁止，且它是 BigInt 精确有理数实现 —— 抄不对还会给出**看起来对**的数）。
 * ⇒ 只提示"期数会缩短 / 每期金额会下降"，**不给具体金额**。
 */

import {
  centsToYuanText,
  type Cents,
  type LoanPeriodView,
  type LoanScheduleSummaryView,
  type PayLoanRequest,
  type PrepayLoanRequest,
} from '@jiaying/shared';
import { yuanTextToCentsOrNull } from './txn-draft.js';

/** 还款的两个 tab（**UI 分组**，不是服务端概念 —— 服务端是两个独立路由） */
export type RepayTab = 'pay' | 'prepay';

/** 提前还款的三个模式（与服务端 `PrepayLoanRequest.mode` 同值域） */
export type PrepayMode = 'shorten' | 'reduce' | 'settle';

/** 一期的可编辑金额（**文本态** —— 与 `account-draft` / `contra-settle` 同一取向） */
export interface PeriodEdit {
  principalText: string;
  interestText: string;
}

/** 还款表单草稿（一个对象装两个 tab 的状态 —— 切 tab 不清空，用户来回看不会白填） */
export interface RepayDraft {
  tab: RepayTab;
  /** 还款日（`YYYY-MM-DD`） */
  dateText: string;
  /** 付款账户 id；`''` = 未选 */
  fromAccountId: string;
  note: string;
  /** 勾选的期次（`periodIndex`）；🔴 **允许不连续**（先还第 3 期不还第 2 期是真实行为） */
  selected: readonly number[];
  /** 逐期编辑（键 = `String(periodIndex)`）；**没编辑过的期次不出现在这里** */
  edits: Readonly<Record<string, PeriodEdit>>;
  prepayMode: PrepayMode;
  /** 提前还款金额（元，文本）；`settle` 时**忽略它**（用服务端的剩余本金） */
  prepayAmountText: string;
}

/** 全部 `pending` 期次（时间升序 —— 服务端已按期号排好，本函数不再排序） */
export function pendingPeriodsOf(schedule: LoanScheduleSummaryView): readonly LoanPeriodView[] {
  return schedule.periods.filter((p) => p.status === 'pending');
}

/**
 * 该贷款**能不能**还（`DR-049` D1）。
 *
 * 🔴 三条都可能挡：计划表未就绪（参数不全）· 无待还期次（已结清）· 计划表为空。
 * ⇒ 入口按钮按它决定**画不画**（不画灰项 —— ㊱ 批的取向）。
 */
export function canRepay(schedule: LoanScheduleSummaryView | undefined): boolean {
  if (schedule === undefined) return false;
  if (!schedule.ready) return false;
  return pendingPeriodsOf(schedule).length > 0;
}

/** 不能还的原因（`canRepay === true` 时返回 `null`）—— 端侧直接展示，不翻译服务端口径 */
export function repayBlockedReason(schedule: LoanScheduleSummaryView | undefined): string | null {
  if (schedule === undefined) return null; // 还没取到：不提示（加载态）
  if (!schedule.ready) return schedule.notReadyReason ?? '这笔贷款的参数还不全，先补上利率 / 期数才能生成还款计划。';
  if (pendingPeriodsOf(schedule).length === 0) return '这笔贷款已经没有待还期次了（已结清或期次全作废）。';
  return null;
}

/**
 * 默认草稿：**勾最早一个 `pending` 期**（`DR-049` D2 的"常规还款"场景）。
 *
 * `prepayMode` 默认 `reduce`（降额）—— 用户对"月供变少"的直觉比"期数变短"更稳；
 * 三个选项在界面上都摆着，不藏。
 */
export function defaultRepayDraft(
  schedule: LoanScheduleSummaryView | undefined,
  today: string,
  fromAccountId = '',
): RepayDraft {
  const first = schedule === undefined ? undefined : pendingPeriodsOf(schedule)[0];
  return {
    tab: 'pay',
    dateText: today,
    fromAccountId,
    note: '',
    selected: first === undefined ? [] : [first.periodIndex],
    edits: {},
    prepayMode: 'reduce',
    prepayAmountText: '',
  };
}

/** 勾 / 取消勾某一期（返回新草稿） */
export function togglePeriod(draft: RepayDraft, periodIndex: number): RepayDraft {
  const has = draft.selected.includes(periodIndex);
  const selected = has
    ? draft.selected.filter((i) => i !== periodIndex)
    : [...draft.selected, periodIndex].sort((a, b) => a - b);
  return { ...draft, selected };
}

/**
 * 某一期的金额文本（**没编辑过 / 编辑框留空 ⇒ 服务端下发的计划值**）。
 *
 * 🔴 **空串 = "没填"**，不是"填了 0" —— 用户只改本金时利息框往往是空的，
 * 那必须回退到计划值；要表达"利息为 0"请显式填 `0`（本批实测踩到这个：
 * 首版把空串原样回显，用户只改本金就得到一个空利息框，提交时被判格式错）。
 */
export function periodAmountTexts(period: LoanPeriodView, draft: RepayDraft): PeriodEdit {
  const edited = draft.edits[String(period.periodIndex)];
  const plan: PeriodEdit = {
    principalText: centsToYuanText(period.principal),
    interestText: centsToYuanText(period.interest),
  };
  if (edited === undefined) return plan;
  return {
    principalText: edited.principalText.trim() === '' ? plan.principalText : edited.principalText,
    interestText: edited.interestText.trim() === '' ? plan.interestText : edited.interestText,
  };
}

/** 改写某一期的金额（返回新草稿） */
export function withPeriodEdit(
  draft: RepayDraft,
  periodIndex: number,
  patch: Partial<PeriodEdit>,
): RepayDraft {
  const key = String(periodIndex);
  const current = draft.edits[key] ?? { principalText: '', interestText: '' };
  return {
    ...draft,
    edits: { ...draft.edits, [key]: { ...current, ...patch } },
  };
}

export type RepayBuildResult =
  | { ok: true; request: PayLoanRequest }
  | { ok: false; errors: readonly string[] };

/**
 * 草稿 → **还本期**请求。
 *
 * 🔴 逐期金额**只有用户改过才发**（铁律 63）：没改 ⇒ 不带 `principal` / `interest`
 * ⇒ 服务端用计划值。**不把计划值抄进请求** —— 那会让"服务端重算过计划表"与
 * "端侧手里的旧快照"打架，用户会被记成错数。
 */
export function buildPayRequest(
  draft: RepayDraft,
  schedule: LoanScheduleSummaryView,
): RepayBuildResult {
  const errors: string[] = [];
  if (!/^\d{4}-\d{2}-\d{2}$/.test(draft.dateText.trim())) errors.push('日期格式须为 YYYY-MM-DD');
  if (draft.fromAccountId.trim() === '') errors.push('请选择付款账户');
  if (draft.selected.length === 0) errors.push('请至少选择一期还款');

  const byIndex = new Map(pendingPeriodsOf(schedule).map((p) => [p.periodIndex, p]));
  const periods: { periodIndex: number; principal?: number; interest?: number }[] = [];

  for (const idx of draft.selected) {
    const period = byIndex.get(idx);
    // 端侧能判的只有"这一期还在不在待还里"（服务端会再判一次）
    if (period === undefined) {
      errors.push(`第 ${String(idx + 1)} 期已不是待还状态，请刷新后重试`);
      continue;
    }
    const edited = draft.edits[String(idx)];
    if (edited === undefined) {
      periods.push({ periodIndex: idx });
      continue;
    }
    // 🔴 **空串 = 没填 ⇒ 不带该键**（用计划值）。若两个框都空，等价于"这一期没改过"。
    const pText = edited.principalText.trim();
    const iText = edited.interestText.trim();
    const principal = pText === '' ? undefined : yuanTextToCentsOrNull(pText);
    const interest = iText === '' ? undefined : yuanTextToCentsOrNull(iText);
    if (principal === null) errors.push(`第 ${String(idx + 1)} 期的本金格式不对（最多两位小数、不得为负）`);
    if (interest === null) errors.push(`第 ${String(idx + 1)} 期的利息格式不对（最多两位小数、不得为负）`);
    if (principal === null || interest === null) continue;
    if (principal === undefined && interest === undefined) {
      periods.push({ periodIndex: idx });
      continue;
    }
    // 只改了一个框时，另一个用计划值参与"本息合计是否为 0"的判定（不发给服务端）
    const effectivePrincipal = principal ?? (period.principal as number);
    const effectiveInterest = interest ?? (period.interest as number);
    if (effectivePrincipal + effectiveInterest <= 0) {
      errors.push(`第 ${String(idx + 1)} 期的本息合计为 0，无法还款`);
      continue;
    }
    periods.push({
      periodIndex: idx,
      ...(principal === undefined ? {} : { principal }),
      ...(interest === undefined ? {} : { interest }),
    });
  }

  if (errors.length > 0) return { ok: false, errors };

  return {
    ok: true,
    request: {
      date: draft.dateText.trim(),
      fromAccountId: draft.fromAccountId.trim(),
      periods,
      ...(draft.note.trim() === '' ? {} : { note: draft.note.trim() }),
    },
  };
}

/** 结清时**自动填**的金额文本（= 计划表口径剩余本金；`DR-049` D4） */
export function settleAmountText(schedule: LoanScheduleSummaryView): string {
  return schedule.scheduleRemainingCents === null ? '' : centsToYuanText(schedule.scheduleRemainingCents);
}

export type PrepayBuildResult =
  | { ok: true; request: PrepayLoanRequest }
  | { ok: false; errors: readonly string[] };

/**
 * 草稿 → **提前还款**请求。
 *
 * 🔴 `settle` 的金额**取服务端的 `scheduleRemainingCents`**，不信用户输入 ——
 * 服务端要求它严格等于 `Σ pending 本金`，端侧若用"腿派生余额"或用户随手填的数会必然 400。
 */
export function buildPrepayRequest(
  draft: RepayDraft,
  schedule: LoanScheduleSummaryView,
): PrepayBuildResult {
  const errors: string[] = [];
  if (!/^\d{4}-\d{2}-\d{2}$/.test(draft.dateText.trim())) errors.push('日期格式须为 YYYY-MM-DD');
  if (draft.fromAccountId.trim() === '') errors.push('请选择付款账户');

  const remaining = schedule.scheduleRemainingCents;
  if (remaining === null || remaining <= 0) {
    return { ok: false, errors: ['这笔贷款没有待还本金，无需提前还款'] };
  }

  let amount: number | null = null;
  if (draft.prepayMode === 'settle') {
    amount = remaining as number;
  } else {
    amount = yuanTextToCentsOrNull(draft.prepayAmountText);
    if (amount === null || amount <= 0) {
      errors.push('提前还款金额须为大于 0 的数字，最多两位小数');
    } else if (amount >= remaining) {
      errors.push('提前还款金额必须小于剩余本金；要全部还清请选「提前结清」');
    }
  }

  if (errors.length > 0 || amount === null) return { ok: false, errors };

  return {
    ok: true,
    request: {
      date: draft.dateText.trim(),
      fromAccountId: draft.fromAccountId.trim(),
      mode: draft.prepayMode,
      amount,
      ...(draft.note.trim() === '' ? {} : { note: draft.note.trim() }),
    },
  };
}

/** 三个 prepay 模式的中文名与一句解释（顺序 = 界面顺序） */
export const PREPAY_MODE_OPTIONS: readonly { value: PrepayMode; label: string; hint: string }[] = [
  { value: 'reduce', label: '降额', hint: '期数不变，每期还款额下降' },
  { value: 'shorten', label: '缩期', hint: '每期还款额不变，期数缩短（总利息更省）' },
  { value: 'settle', label: '提前结清', hint: '后续期次全部作废，一次还清剩余本金' },
];

/** 结清金额只读时的说明（用户会问"为什么不能改"） */
export const SETTLE_AMOUNT_LOCKED_HINT =
  '提前结清的金额固定等于剩余本金（多一分少一分都不成立），由系统按计划表算好，不需要填。';

/** 结清是不可逆的（期次全部作废）—— 给一次明说的代价提示 */
export const SETTLE_CONFIRM_HINT =
  '提前结清会把后续所有期次作废（作废的期次仍留在明细里，但不再计入必付与剩余本金）。'
  + '只有确定要一次还清时才用；只还一部分请选「降额」或「缩期」。';

/** 还本期的说明（把"利息计支出、本金不计"讲清 —— 用户最常问这个） */
export const REPAY_CALIBER_HINT =
  '本期还款会拆成三笔：本金（还债，不计收支）+ 利息（计入支出）+ 贷款余额减少。'
  + '本金与利息可按银行实际数改，改完写回还款计划。';

/** 金额输入框标签（带币种，防"以为对方是美元"） */
export function amountLabel(currency: string): string {
  return `金额（${currency}）`;
}

/** 一期的展示标题（`第 3 期 · 2026-03-31`） */
export function periodTitle(period: LoanPeriodView): string {
  return `第 ${String(period.periodIndex + 1)} 期 · ${period.dueDate}`;
}

/** 一期的计划金额文本（`本金 ¥800.00 · 利息 ¥120.00`）—— 只用服务端下发的数 */
export function periodPlanText(period: LoanPeriodView, currency: string): string {
  const p = centsToYuanText(period.principal);
  const i = centsToYuanText(period.interest);
  return `本金 ${currency} ${p} · 利息 ${currency} ${i}`;
}

/** 还款成功后的一句话（金额来自服务端结果，端侧不自己求和） */
export function payDoneText(principalCents: Cents, interestCents: Cents, currency: string): string {
  return `已还本金 ${currency} ${centsToYuanText(principalCents)} · 利息 ${currency} ${centsToYuanText(interestCents)}`;
}
